import Loader from 'components/Loader';
import SmkAsyncSelect from 'components/SmkAsyncSelect';
import { useFormik } from 'formik';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { toast } from 'react-toastify';
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
  Table,
} from 'reactstrap';
import { API_BASE_URL, shortToFullDay } from 'utils/constants';
import { SMKLinks } from 'utils/links';
import * as Yup from 'yup';
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import TimingsTableRows from '../../../components/TimingsTableRows';
import useRequest from '../../../hooks/useRequest';
import { buildURL, titleCaseIfExists } from '../../../utils/helper';
import { HEATLHCARE_HEADERS, parseAvailabilityData } from '../helpers';
import { Link } from 'react-router-dom';
import UpdateHealthCareFacility from '../HealthCareFacilityList/updateHealthCareFacility';

const AddUpdateClinic = () => {
  const { doctorId, id: clinicId } = useParams();
  const history = useHistory();

  toast.configure();
  const [clinicDetails, setClinicDetails] = useState();
  const drAvailabilityData = !isEmpty(clinicDetails) ? parseAvailabilityData(clinicDetails?.timings) : [];

  const defaultTimings = {
    Sun: [],
    Mon: [],
    Tue: [],
    Wed: [],
    Thu: [],
    Fri: [],
    Sat: [],
  };

  const [addRowInTimings, setAddRowInTimings] = useState(defaultTimings);

  const addRowTable = (weekDay) => {
    const data = {
      startTime: new Date().setHours(10, 0, 0, 0),
      endTime: new Date().setHours(18, 0, 0, 0),
    };
    setAddRowInTimings({
      ...addRowInTimings,
      [weekDay]: [...addRowInTimings[weekDay], data],
    });
  };

  const tableRowRemove = (index, weekDay) => {
    const dataRow = [...addRowInTimings[weekDay]];
    dataRow.splice(index, 1);
    setAddRowInTimings({
      ...addRowInTimings,
      [weekDay]: dataRow,
    });
  };

  const [getDoctorClinicDetails, { loading: getDoctorClinicLoading }] = useRequest({
    ...HEATLHCARE_HEADERS,
    url: `/healthcare/doctor/admin/locations/${clinicId}`,
    method: 'GET',
    onSuccess: (data) => {
      if (!isEmpty(data?.timings)) {
        const timingsData = reverseTimingsFormat(data?.timings);
        setAddRowInTimings({
          ...defaultTimings,
          ...timingsData,
        });
      }
      setClinicDetails(data);
    },
    onError: (err) => {
      console.warn(err);
    },
  });

  const [addDoctorClinicRequest, { loading: addDoctorClinicLoading }] = useRequest({
    ...HEATLHCARE_HEADERS,
    url: `/healthcare/doctor/admin/${doctorId}/location`,
    method: 'POST',
    onSuccess: () => {
      const url = buildURL(SMKLinks.HEALTHCARE_MODULE_DOCTOR_DETAILS, {
        id: doctorId,
      });
      history.replace(url);
      toast.success('Success! Clinic has been added.');
    },
    onError: (err) => {
      toast.error(err?.message);
      console.log(err);
    },
  });

  const [updateDoctorClinicRequest, { loading: updateDoctorClinicLoading }] = useRequest({
    ...HEATLHCARE_HEADERS,
    url: `/healthcare/doctor/admin/${doctorId}/location/${clinicId}`,
    method: 'PUT',
    onSuccess: () => {
      window.location.reload();
      toast.success('Success! Clinic has been updated.');
    },
    onError: (err) => {
      toast.error(titleCaseIfExists(err?.message));
      console.log(err);
    },
  });

  const editInitialValues = () => {
    return {
      active: clinicDetails?.active,
      deleted: clinicDetails?.deleted,
      healthcareFacility: !isEmpty(clinicDetails?.healthcareFacility)
        ? {
            label: clinicDetails?.healthcareFacility?.name,
            value: clinicDetails?.healthcareFacilityId,
          }
        : null,
      defaultCharges: clinicDetails?.defaultCharges,
      notes: clinicDetails?.notes,
    };
  };

  const getInitialValues = () => ({
    active: true,
    deleted: false,
    healthcareFacility: null,
    timings: '',
    defaultCharges: '',
    registrationFee: '',
    firstVisit: '',
    followUpVisit: '',
    notes: '',
    registrationFeeCurrency: { label: 'INR', value: 'inr' },
    firstVisitCurrency: { label: 'INR', value: 'inr' },
    followUpVisitCurrency: { label: 'INR', value: 'inr' },
  });

  const reverseTimingsFormat = (data) => {
    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const reversedData = {
      Sun: [],
      Mon: [],
      Tue: [],
      Wed: [],
      Thu: [],
      Fri: [],
      Sat: [],
    };

    data.forEach(({ days, timing }) => {
      const dayRows = timing.map((timeRange) => {
        const [startTime, endTime] = timeRange.split(' - ');

        const parseTime = (timeStr) => {
          const [time, modifier] = timeStr.trim().split(' ');
          let [hours, minutes] = time.split(':').map(Number);

          if (modifier === 'PM' && hours < 12) hours += 12;
          if (modifier === 'AM' && hours === 12) hours -= 12;

          return new Date(1970, 0, 1, hours, minutes);
        };

        const start = parseTime(startTime);
        const end = parseTime(endTime);

        return {
          startTime: start.toISOString(),
          endTime: end.toISOString(),
        };
      });

      // Handle day ranges and single days
      if (days.includes('-')) {
        const [startDay, endDay] = days.split('-').map((d) => d.trim());
        const startIndex = weekdays.indexOf(startDay);
        const endIndex = weekdays.indexOf(endDay);

        if (startIndex !== -1 && endIndex !== -1) {
          for (let i = startIndex; i <= endIndex; i++) {
            reversedData[weekdays[i]] = dayRows;
          }
        }
      } else {
        if (weekdays.includes(days)) {
          reversedData[days] = dayRows;
        }
      }
    });

    return reversedData;
  };

  const timingsFormat = (data) => {
    const timings = [];

    Object.keys(data).forEach((weekDay) => {
      const dayRows = data[weekDay];
      if (dayRows.length > 0) {
        const timing = dayRows.map((row) => {
          const start = new Date(row.startTime);
          const end = new Date(row.endTime);

          let startTime = start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
          let endTime = end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });

          startTime = startTime.replace('am', 'AM').replace('pm', 'PM');
          endTime = endTime.replace('am', 'AM').replace('pm', 'PM');

          return `${startTime} - ${endTime}`;
        });

        timings.push({
          days: weekDay,
          timing: timing,
        });
      }
    });

    return timings;
  };

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: false,
    initialValues: isEmpty(clinicDetails) ? { ...getInitialValues() } : { ...editInitialValues() },
    onSubmit: (values) => {
      const timings = timingsFormat(addRowInTimings);

      if (isEmpty(values?.healthcareFacility?.value)) {
        toast.error('Please provide Healthcare facility');
        return;
      }

      const payload = {
        active: values?.active,
        deleted: values?.deleted,
        healthcareFacilityId: values?.healthcareFacility?.value,
        defaultCharges: values?.defaultCharges,
        notes: values?.notes,
        timings,
        doctorId,
      };

      isEmpty(clinicDetails)
        ? addDoctorClinicRequest({
            body: payload,
          })
        : updateDoctorClinicRequest({
            body: payload,
          });
    },
    validationSchema: Yup.object().shape({
      healthcareFacility: Yup.object()
        .required('Please provide Healthcare facility')
        .nullable(true),
      // defaultCharges: Yup.string().required('Please provide defaultCharges'),
      // notes: Yup.string().required('Please provide notes'),
    }),
  });

  useEffect(() => {
    if (clinicId) {
      getDoctorClinicDetails();
    }
  }, [clinicId]);

  const handleCopyDayValue = () => {
    const mondayValue = addRowInTimings['Mon'];

    if (isEmpty(mondayValue)) {
      alert('Please add timings for Monday before copying.');
      return '';
    }

    const newData = {};
    Object.keys(addRowInTimings)?.forEach((day) => {
      newData[day] = [...mondayValue];
    });
    setAddRowInTimings(newData);
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Loader isActive={updateDoctorClinicLoading || addDoctorClinicLoading || getDoctorClinicLoading} />
        <Breadcrumbs title="Clinic" breadcrumbItem={`${isEmpty(clinicId) ? 'Add' : 'Update'} Clinic`} />
        <Form onSubmit={formik.handleSubmit}>
          <Form onSubmit={formik.handleSubmit}>
            <Row>
              <Col md={9}>
                <Card>
                  <CardBody>
                    <FormGroup className="mb-4">
                      <Label className="form-label">
                        Healthcare Facility <span className="text-danger">*</span>{' '}
                        <a
                          target="_blank"
                          href={buildURL(SMKLinks.HEALTHCARE_MODULE_EDIT_HEALTH_CARE, {
                            id: clinicDetails?.healthcareFacilityId,
                          })}>
                          Edit
                        </a>
                      </Label>
                      <SmkAsyncSelect
                        isHealthcare={true}
                        acceptedKey={'name'}
                        acceptedValue={'id'}
                        searchKeyName="name"
                        placeholder="Type healthcare name.."
                        fetchUrl={`${API_BASE_URL}/healthcare/healthcare-facility/admin/search`}
                        filters={{
                          autoComplete: true,
                          page: 1,
                          size: 100,
                          sortBy: 'name',
                          sortOrder: 1,
                        }}
                        onChange={(res) => {
                          formik.setFieldValue('healthcareFacility', {
                            label: res?.name,
                            value: res?.id,
                          });
                        }}
                        value={
                          !isEmpty(formik.values.healthcareFacility?.value) ? formik.values.healthcareFacility : null
                        }
                      />
                      <FormFeedback className="d-block">{formik.errors.healthcareFacility}</FormFeedback>
                    </FormGroup>

                    <FormGroup className="mb-4">
                      <Label className="form-label">Default Charges</Label>
                      <Input
                        id="defaultCharges"
                        type="text"
                        className="form-control"
                        placeholder="Default Charges..."
                        invalid={!!(formik.touched.defaultCharges && formik.errors.defaultCharges)}
                        {...formik.getFieldProps('defaultCharges')}
                        error={!isEmpty(formik.errors.defaultCharges)}
                      />
                      <FormFeedback className="d-block">{formik.errors.defaultCharges}</FormFeedback>
                    </FormGroup>
                    <FormGroup className="mb-4">
                      <Label className="form-label">Notes</Label>
                      <Input
                        id="notes"
                        type="textarea"
                        rows="3"
                        className="form-control"
                        placeholder="Notes..."
                        invalid={!!(formik.touched.notes && formik.errors.notes)}
                        {...formik.getFieldProps('notes')}
                        error={!isEmpty(formik.errors.notes)}
                      />
                      <FormFeedback className="d-block">{formik.errors.notes}</FormFeedback>
                    </FormGroup>
                  </CardBody>
                </Card>

                <Card>
                  <CardBody>
                    <div className="mb-4">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <h5 className="mb-0">
                          Timings <span className="text-danger">*</span>
                        </h5>

                        <button className="btn btn-sm btn-success" type="button" onClick={handleCopyDayValue}>
                          Copy Monday Timings to All Days
                        </button>
                      </div>
                      <FormFeedback className="d-block mt-2">{formik.errors.timings}</FormFeedback>
                    </div>
                    <Table className="table-nowrap mb-0 table-white">
                      <tbody>
                        {Object.keys(addRowInTimings).map((weekDay) => (
                          <React.Fragment key={weekDay}>
                            <tr>
                              <td>{shortToFullDay[weekDay]}</td>
                              <td colSpan={6}></td>
                              <td style={{ verticalAlign: 'middle' }}>
                                {addRowInTimings[weekDay]?.length < 3 && (
                                  <button
                                    type="button"
                                    onClick={() => addRowTable(weekDay)}
                                    className="btn btn-light rounded-circle">
                                    <i className="bx bx-plus" />
                                  </button>
                                )}
                              </td>
                            </tr>
                            <TimingsTableRows
                              rows={addRowInTimings[weekDay]}
                              tableRowRemove={tableRowRemove}
                              addRowInTimings={addRowInTimings}
                              setAddRowInTimings={setAddRowInTimings}
                              weekDay={weekDay}
                            />
                          </React.Fragment>
                        ))}
                      </tbody>
                    </Table>
                  </CardBody>
                </Card>

                <div className="d-flex align-items-center mb-4 mt-5 border-bottom border-white  pb-3">
                  <h3 className="mb-0">Healthcare Facility Details </h3>
                  <a
                    className="text-xl ms-3"
                    target="_blank"
                    href={buildURL(SMKLinks.HEALTHCARE_MODULE_EDIT_HEALTH_CARE, {
                      id: clinicDetails?.healthcareFacilityId,
                    })}>
                    <i className="fas fa-pencil-alt text-success text-xl " />
                  </a>
                </div>

                <UpdateHealthCareFacility isDisabled={true} details={clinicDetails?.healthcareFacility} />
              </Col>

              <Col md={3}>
                <Card>
                  <CardBody>
                    <Button type="submit" color="primary" className="w-100 mb-3">
                      {`${isEmpty(clinicId) ? 'Add' : 'Update'}`} Clinic
                    </Button>

                    <Button
                      className="w-100"
                      color="light"
                      onClick={() => {
                        const url = buildURL(SMKLinks.HEALTHCARE_MODULE_DOCTOR_DETAILS, {
                          id: doctorId,
                        });
                        history.replace(url);
                      }}>
                      Cancel
                    </Button>
                  </CardBody>
                </Card>

                <Card>
                  <CardBody>
                    {[
                      {
                        name: 'Active',
                        value: 'active',
                      },
                      {
                        name: 'Deleted',
                        value: 'deleted',
                      },
                    ]?.map((data) => (
                      <div>
                        <CardTitle className="mb-3">{data?.name}</CardTitle>
                        {[true, false].map((option, i) => (
                          <div key={`${data?.value}_${i}`} className="form-check mb-3 d-inline-block me-4">
                            <input
                              type="radio"
                              id={`${data?.value}_${option}`}
                              name={data?.value}
                              className="form-check-input"
                              checked={formik.values[data?.value] === option}
                              onChange={() => formik.setFieldValue(data?.value, option)}
                            />
                            <label className="form-check-label" htmlFor={`${data?.value}_${option}`}>
                              {option ? 'Yes' : 'No'}
                            </label>
                          </div>
                        ))}
                      </div>
                    ))}
                  </CardBody>
                </Card>

                <Card>
                  <CardBody>
                    <div className="doc-information-details mb-0" id="doc_business_hours">
                      <h4 className="h6 mb-3">Timings</h4>
                      {!isEmpty(drAvailabilityData) && (
                        <div className="awards-icon-info p-3 border rounded-2 bg-white">
                          <ul className="business-hours mb-0 ps-0">
                            {drAvailabilityData.map((schedule, idx) => (
                              <li
                                className={`d-flex justify-content-between   ${
                                  idx === drAvailabilityData.length - 1 ? '' : 'border-bottom pb-2 mb-2 '
                                }`}
                                key={schedule.day}>
                                <h6 className="mb-0">{schedule.day}</h6>
                                <p className="mb-0">
                                  <ul style={{ listStyle: 'none' }}>
                                    {schedule?.timeSlots?.map((time, index) => (
                                      <li key={index}>{time}</li>
                                    ))}
                                  </ul>
                                </p>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Form>
        </Form>
      </Container>
    </div>
  );
};

export default AddUpdateClinic;
